// Externals
import { useMemo, useRef } from 'react';

// Libraries
import { useShowroomNavConfigSelector } from '@makemydeal/dr-sales-checkin-store';
import { type ShowroomSectionKey } from '@makemydeal/dr-sales-checkin-types';
import { isShowroomSectionKey } from '@makemydeal/dr-sales-checkin-ui-utils';

// Behaviour layer
import { SIDE_NAV_GROUPS } from '../constants';
import { buildNavViewItems } from './showroomNav/buildNavViewItems';
import { BUILD_YOUR_OFFER_GROUP, sectionMetaByGroup } from './showroomNav/navItemRegistry';
import { useNavViewModel } from './showroomNav/useNavViewModel';
import type {
    LeftNavSection,
    ShowroomNavViewItem,
    ShowroomSectionComponents,
    ShowroomSectionItem,
    SideNavViewItem
} from '../types/showroomNavTypes';

/**
 * Narrows a nav item to one the page can render as a section. Only a Build Your Offer card whose key
 * is a section gets a `component`, so its presence is the whole test — and it narrows the item's
 * `key` along with it.
 */
export const isShowroomSectionItem = (item: ShowroomNavViewItem): item is ShowroomSectionItem => 'component' in item;

/**
 * The one config-driven view model for the Showroom (US2055174, US2150169), and the single source of
 * truth for both of its consumers: `SideBarNav` renders `sections`, and `ShowroomSectionStack`
 * renders the Build Your Offer group's items from the same `sections` and hands `sectionKeys` to the
 * scroll engine. Reading them from one pass is what keeps the page and the sidebar in step — a card
 * the dealer's edition hides is absent from both, and the page cannot drift into its own idea of
 * which sections exist, which ones they are, or what they are called.
 *
 * `sections` builds the `LEFT_NAV` (cards) and `QUICK_LINK` (links) groups from the unified
 * `entities`, sharing `buildNavViewItems` with the header hook. Pass `sectionComponents` and each
 * Build Your Offer card that is a section also carries the `component` that renders it, so the page
 * gets what to render beside what to call it; `isShowroomSectionItem` is how a caller picks those
 * items out. The sidebar passes nothing and sees the items unchanged.
 *
 * `sectionKeys` is that same narrowing as a plain ordered list for the scroll engine, and keeps a
 * stable identity across renders, cached by its joined signature. `sections` cannot: its `ctx`
 * carries a fresh capability reader on every render. The engine's effects key off the cached keys,
 * so handing them a new array each render would re-run them on every render.
 */

/**
 * TODO: HL
 * - Break useFeatureConfig (this is right now serving BuildYourOffer component and SideBar)
 * - We need to create useBuildYourOffer hook that would internally call the useFeatureConfig.
 * - useFeatureConfig should only returns us the feature config IFeatureConfig
 * interface IFeatureConfig {
 * key: string;
 * title: string;
 * order: number;
 * components: React.ReactElement;
 * }
 */
export const useFeatureConfig = (sectionComponents?: ShowroomSectionComponents) => {
    // 1. The two inputs. `entities` is the serializable config — which nav items exist, which group
    //    each belongs to, whether it is visible. `ctx` is everything live that the config cannot
    //    carry: the customer names, the deal state the rules read, the active section, the dealer's
    //    capability gate. Both nav hooks read the same pair, which is why the header, the sidebar and
    //    the page can never disagree about what the dealer has.
    const { entities } = useShowroomNavConfigSelector();
    const ctx = useNavViewModel();

    const sections = useMemo<LeftNavSection[]>(
        () =>
            // 2. One group at a time, in `SIDE_NAV_GROUPS` order: Build Your Offer, then Quick Links.
            //    The group's key and title come from the registry, never from a literal here.
            SIDE_NAV_GROUPS.map((group) => ({
                key: sectionMetaByGroup[group]!.key,
                title: sectionMetaByGroup[group]!.title,
                // 3. `buildNavViewItems` does the real assembly — capability gate, visibility flag,
                //    hide/disable rules, registry presentation, commands, then sort by order. Shared
                //    with `useHeaderNavbarConfig`, so a rule change lands everywhere at once.
                items: (buildNavViewItems(entities, group, ctx) as SideNavViewItem[]).map<ShowroomNavViewItem>((item) =>
                    // 4. The page's half of the config: a caller that passed a registry gets the
                    //    section component attached to each card that is a section, so the page has
                    //    what to render beside what to call it. `key` is restated to narrow its type
                    //    with the item (see `ShowroomSectionItem`). A card with no section — and every
                    //    quick link — is handed back untouched, which is all the sidebar ever sees.
                    sectionComponents && isShowroomSectionKey(item.key)
                        ? { ...item, key: item.key, component: sectionComponents[item.key] }
                        : item
                )
                // 5. A group the dealer has nothing in would otherwise render as a bare heading
                //    above a divider.
            })).filter((section) => section.items.length > 0),
        [entities, ctx, sectionComponents]
    );

    // 6. The scroll engine's input: the Build Your Offer cards as a plain ordered list of keys. The
    //    same narrowing as step 4, so the page stacks exactly the cards the sidebar shows — and a
    //    card that is not one of the sections is dropped here rather than reaching the registry and
    //    resolving to `undefined`.
    const resolved = useMemo<ShowroomSectionKey[]>(() => {
        const buildYourOffer = sections.find((section) => section.key === BUILD_YOUR_OFFER_GROUP);

        return (buildYourOffer?.items ?? []).reduce<ShowroomSectionKey[]>(
            (acc, item) => (isShowroomSectionKey(item.key) ? [...acc, item.key] : acc),
            []
        );
    }, [sections]);

    // 7. Hold the keys still. `useMemo` cannot: `ctx` carries a fresh capability reader on every
    //    render, so step 2 recomputes every render and step 6 with it. The engine keys its arrival,
    //    command and spy effects off this array, so a new one each render would tear them down and
    //    re-run them on every render — the page would re-arrive mid-scroll. Comparing the joined keys
    //    rather than the array means only a section actually appearing or disappearing is a change.
    const signature = resolved.join('|');
    const cacheRef = useRef<{ signature: string; sectionKeys: ReadonlyArray<ShowroomSectionKey> }>({
        signature,
        sectionKeys: resolved
    });

    if (cacheRef.current.signature !== signature) {
        cacheRef.current = { signature, sectionKeys: resolved };
    }

    return { sections, sectionKeys: cacheRef.current.sectionKeys };
};
